// Optimistic predictions for the core commands: what the store shows before the server confirms.

import { RANK_KEY, uuidv7, type FieldDef, type Id, type LinkObj, type RecordObj } from '@confdeck/core-shared';
import type { SyncClient } from './client.ts';
import type { PendingOp } from './store.ts';

const now = () => new Date().toISOString();

/** Link ops that make a relation field hold exactly `ids`, mirroring the server's setRelation. */
function relationOps(client: SyncClient, rec: { id: Id }, field: FieldDef, ids: Id[]): PendingOp[] {
  const { store } = client;
  const target = store.meta.relationTarget(field);
  if (!target) return [];
  const fromSide = field.options.side !== 'to';
  const relId = target.relation.id;
  const ops: PendingOp[] = [];
  const existing = store.linkObjects(relId, rec.id, fromSide ? 'from' : 'to');
  const other = (l: LinkObj) => (fromSide ? l.toId : l.fromId);
  for (const l of existing) if (!ids.includes(other(l))) ops.push({ kind: 'link', id: l.id, op: 'patch', data: { archivedAt: now() } });
  const have = new Set(existing.map(other));
  const otherSingle = target.relation.cardinality === 'one-to-one' || (target.relation.cardinality === 'many-to-one' && !fromSide);
  for (const id of ids) {
    if (have.has(id)) continue;
    if (otherSingle) for (const l of store.linkObjects(relId, id, fromSide ? 'to' : 'from')) ops.push({ kind: 'link', id: l.id, op: 'patch', data: { archivedAt: now() } });
    const link: LinkObj = { id: uuidv7(), kind: 'link', relationId: relId, fromId: fromSide ? rec.id : id, toId: fromSide ? id : rec.id, data: {}, createdAt: now(), rev: 0, archivedAt: null };
    ops.push({ kind: 'link', id: link.id, op: 'put', data: link as unknown as Record<string, unknown> });
  }
  return ops;
}

/**
 * Records just made with «+ Запись» in this tab (R-U4, audit 28.10 L10): the card puts the cursor into the title, and Esc before any
 * edit drops the empty record instead of leaving «Без названия» behind. Any edit makes the record a kept one.
 */
export const freshRecords = new Set<Id>();

export function createRecord(client: SyncClient, typeId: Id, data: Record<string, unknown>, ownerId?: Id | null): Id {
  const id = uuidv7();
  const plain: Record<string, unknown> = {};
  const ops: PendingOp[] = [];
  for (const [k, v] of Object.entries(data)) {
    const f = client.store.meta.field(typeId, k);
    if (f?.fieldType === 'relation') ops.push(...relationOps(client, { id }, f, (Array.isArray(v) ? v : v ? [v] : []) as Id[]));
    else plain[k] = v;
  }
  const rec: RecordObj = { id, kind: 'record', typeId, data: plain, ownerId: ownerId ?? client.header?.userId ?? null, createdBy: client.header?.userId ?? null, createdAt: now(), updatedAt: now(), rev: 0, archivedAt: null };
  ops.unshift({ kind: 'record', id, op: 'put', data: rec as unknown as Record<string, unknown> });
  client.mutate('record.create', { id, typeId, data, ownerId: rec.ownerId }, ops);
  return id;
}

/**
 * Sets one field. Relation fields take a list of record ids. `rev` makes the change conditional: refused when the record
 * moved on since this copy (a card dragged on a stale board).
 */
export function setField(client: SyncClient, rec: RecordObj, key: string, value: unknown, opts: { rev?: number } = {}): void {
  freshRecords.delete(rec.id);
  const f = client.store.meta.field(rec.typeId, key);
  if (f?.fieldType === 'relation') {
    const ids = (Array.isArray(value) ? value : value ? [value] : []) as Id[];
    client.mutate('record.update', { id: rec.id, data: { [key]: ids } }, relationOps(client, rec, f, ids));
    return;
  }
  setFields(client, rec, { [key]: value ?? null }, opts);
}

/** Sets several plain (not relation) fields in one command: the gantt moves the start and the end of a bar together. */
export function setFields(client: SyncClient, rec: RecordObj, data: Record<string, unknown>, opts: { rev?: number } = {}): void {
  freshRecords.delete(rec.id);
  client.mutate('record.update', { id: rec.id, data, ...(opts.rev !== undefined ? { rev: opts.rev } : {}) }, [{ kind: 'record', id: rec.id, op: 'patch', data: { data, updatedAt: now() } }]);
}

/**
 * Moves a card on a board (decision 031 п.6): a new stage (`data`) and the manual place in one command; `ranks` give places to
 * cards of the column that had none. Shown at once; the server checks the stage like any edit.
 */
export function moveRecord(client: SyncClient, rec: RecordObj, place: { rank: string; ranks?: { id: Id; rank: string }[]; data?: Record<string, unknown>; rev?: number }): void {
  freshRecords.delete(rec.id);
  const ops: PendingOp[] = (place.ranks ?? []).map((r) => ({ kind: 'record' as const, id: r.id, op: 'patch' as const, data: { data: { [RANK_KEY]: r.rank } } }));
  ops.push({ kind: 'record', id: rec.id, op: 'patch', data: { data: { ...(place.data ?? {}), [RANK_KEY]: place.rank }, updatedAt: now() } });
  client.mutate('record.move', {
    id: rec.id, rank: place.rank, ...(place.data ? { data: place.data } : {}), ...(place.ranks?.length ? { ranks: place.ranks } : {}), ...(place.rev !== undefined ? { rev: place.rev } : {}),
  }, ops);
}

export function setOwner(client: SyncClient, rec: RecordObj, ownerId: Id | null): void {
  freshRecords.delete(rec.id);
  client.mutate('record.update', { id: rec.id, ownerId }, [{ kind: 'record', id: rec.id, op: 'patch', data: { ownerId, updatedAt: now() } }]);
}

export function archiveRecords(client: SyncClient, ids: Id[]): void {
  client.mutate('record.archive', { ids }, ids.map((id) => ({ kind: 'record' as const, id, op: 'patch' as const, data: { archivedAt: now() } })));
}

export function restoreRecords(client: SyncClient, ids: Id[]): void {
  client.mutate('record.restore', { ids }, ids.map((id) => ({ kind: 'record' as const, id, op: 'patch' as const, data: { archivedAt: null } })));
}
